/**
 * Pure error/auth helpers — no axios import, fully unit-testable.
 *
 * Founder root-cause Jun 13 2026:
 * > Re-probe banner showed "Re-probed 0 ok · 0 failed (of 0 targets).
 * > Token expired" — silent zeros from React state defaults masquerading
 * > as real API data, because catch blocks did `setResult({ok:false,
 * > tldr: e.message})` and never set the counts.
 *
 * `formatApiError(err)` is the contract: every consumer gets the same
 * shape regardless of what failed (auth / network / 4xx / 5xx /
 * business). Callers MUST use the structured fields (title / message /
 * hint / kind) and MUST NEVER fall back to silent zero defaults.
 */

// ── JWT helpers (decode-only, never verify) ─────────────────────────
function decodeJwtPayload(token) {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? b64 : b64 + '='.repeat(4 - (b64.length % 4));
    return JSON.parse(atob(pad));
  } catch (_e) {
    return null;
  }
}

export function isTokenExpired(token, skewSec = 15) {
  if (!token) return true;
  const p = decodeJwtPayload(token);
  if (!p || !p.exp) return false;   // unknown shape → let the server decide
  const nowSec = Math.floor(Date.now() / 1000);
  return p.exp <= (nowSec + skewSec);
}

export function tokenExpiresAt(token) {
  const p = decodeJwtPayload(token);
  return p && p.exp ? new Date(p.exp * 1000) : null;
}

// ── Error classification ────────────────────────────────────────────

const AUTH_DETAILS = new Set([
  'Token expired', 'Invalid token', 'Missing authorization',
  'token_expired', 'invalid_token',
]);

export function isAuthError(err) {
  if (!err) return false;
  const status = err.response?.status ?? err.status;
  if (status === 401) return true;
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string' && AUTH_DETAILS.has(detail)) return true;
  return false;
}

export function formatApiError(err) {
  // Network error (no response at all)
  if (err && !err.response && (err.code === 'ERR_NETWORK'
      || err.message === 'Network Error')) {
    return {
      ok: false, kind: 'network', status: 0,
      title: 'Network unreachable',
      message: 'Could not reach the server. Check your connection and try again.',
      hint: 'If this keeps happening, the cloud may be down — try again in 30s.',
      retriable: true,
    };
  }
  // Client-side aborts / cancels
  if (err && (err.code === 'ERR_CANCELED' || err.name === 'CanceledError')) {
    return {
      ok: false, kind: 'cancelled', status: 0,
      title: 'Request cancelled',
      message: 'The request was cancelled before completing.',
      hint: '', retriable: true,
    };
  }
  const status = err?.response?.status ?? 0;
  const data = err?.response?.data ?? {};
  const rawDetail = typeof data === 'string'
    ? data
    : (data.detail ?? data.message ?? '');
  // FastAPI validation errors come as a list of {loc, msg, type}; render
  // them as a single readable string instead of "[object Object]".
  let detail = '';
  if (typeof rawDetail === 'string') {
    detail = rawDetail;
  } else if (Array.isArray(rawDetail)) {
    detail = rawDetail
      .map(d => (d && d.msg)
        ? `${(d.loc || []).join('.')}: ${d.msg}`
        : JSON.stringify(d))
      .join('; ');
  } else if (rawDetail && typeof rawDetail === 'object') {
    detail = JSON.stringify(rawDetail);
  }

  if (isAuthError(err)) {
    return {
      ok: false, kind: 'auth_expired', status,
      title: 'Session expired',
      message: 'Your sign-in has expired. Please refresh the page and log in again.',
      hint: 'No data was changed — try the action again after logging back in.',
      retriable: false,
    };
  }
  if (status === 403) {
    return {
      ok: false, kind: 'forbidden', status,
      title: 'Not allowed',
      message: detail || 'You do not have permission to perform this action.',
      hint: 'Ask an admin if you believe this is wrong.',
      retriable: false,
    };
  }
  if (status === 404) {
    return {
      ok: false, kind: 'not_found', status,
      title: 'Not found',
      message: detail || 'The resource was not found.',
      hint: '', retriable: false,
    };
  }
  if (status === 422 || status === 400) {
    return {
      ok: false, kind: 'validation', status,
      title: 'Invalid request',
      message: detail || 'Validation failed.',
      hint: '', retriable: false,
    };
  }
  if (status === 409) {
    return {
      ok: false, kind: 'conflict', status,
      title: 'Conflict',
      message: detail || 'The resource state conflicts with this action.',
      hint: 'Refresh the page to see the latest state and try again.',
      retriable: true,
    };
  }
  if (status === 429) {
    return {
      ok: false, kind: 'rate_limited', status,
      title: 'Too many requests',
      message: 'You\'re going too fast — please wait a moment.',
      hint: '', retriable: true,
    };
  }
  if (status >= 500) {
    return {
      ok: false, kind: 'server', status,
      title: `Server error (${status})`,
      message: detail || 'Something went wrong on the server.',
      hint: 'Try again in a minute. If it persists, the cloud may need a redeploy.',
      retriable: true,
    };
  }
  // Unknown — never default to silent zeros.
  return {
    ok: false, kind: 'unknown', status,
    title: 'Unexpected error',
    message: detail || err?.message || 'An unknown error occurred.',
    hint: '', retriable: true,
  };
}
